import React, { useReducer, useEffect } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  PageContent,
  Container,
  Row,
  Col,
  Field,
  Heading,
  Button,
  Select,
  Datepicker,
} from 'components';
import { globalPropTypes } from 'helpers';
import reducer, { defaultState, updateFieldCreator } from './reducer';
import { Margin } from 'styled-components-spacing';
import clearErrors from 'helpers/clearErrors';
import {
  createIpmaCisCreator,
  loadClientMemberAssociationsByMACreator,
  loadGlobalCisInvoicesAttributesCreator,
  setIpmaCisCreateErrorsCreator,
} from '../../../../../store/actionsCreators';
import moment from 'moment';
import enGB from 'date-fns/locale/en-GB';
import { registerLocale } from 'react-datepicker';

import fr from 'date-fns/locale/fr';
registerLocale('fr', fr);

const AddInvoice = ({
  errors,
  createInvoice,
  removeError,
  ma,
  loadInvoicesAttributes,
  clientMemberAssociations,
  loadClientMemberAssociations,
}) => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(reducer, defaultState);
  const history = useHistory();

  const handleChangeNative = (name, errorName) => (e) => {
    dispatch(updateFieldCreator({ name, value: e.target.value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleChangeCustom = (name, errorName) => (value) => {
    dispatch(updateFieldCreator({ name, value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleChangeSelect = (name, errorName) => (option) => {
    dispatch(updateFieldCreator({ name, value: option?.value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  useEffect(() => {
    loadInvoicesAttributes();
  }, []);

  useEffect(() => {
    if (state.memberAssociation) {
      loadClientMemberAssociations(state.memberAssociation);
    }
  }, [state.memberAssociation]);

  const submit = () => {
    const body = {
      referenceNumber: state.referenceNumber,
      invoiceDate: state.invoiceDate ? moment(state.invoiceDate).format('YYYY-MM-DD') : null,
      invoicePayedDate: state.invoicePayedDate
        ? moment(state.invoicePayedDate).format('YYYY-MM-DD')
        : null,
      vatId: state.vatId,
      memberAssociation: state.memberAssociation,
      clientMemberAssociation: state.clientMemberAssociation,
      dateFrom: state.dateFrom ? moment(state.dateFrom).format('YYYY-MM-DD') : null,
      dateTo: state.dateTo ? moment(state.dateTo).format('YYYY-MM-DD') : null,
    };

    createInvoice(body, history);
  };

  return (
    <PageContent>
      <Container>
        <form>
          <Margin bottom="24">
            <Heading size="h5">{t('page.user.cis.invoices.addInvoice')}</Heading>
          </Margin>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="referenceNumber"
                  label={t('page.user.cis.invoices.referenceNumber')}
                  required
                  error={errors.referenceNumber}
                >
                  <input
                    id="referenceNumber"
                    type="text"
                    placeholder={t('page.user.cis.invoices.referenceNumberPlaceholder')}
                    value={state.referenceNumber}
                    onChange={handleChangeNative('referenceNumber', 'referenceNumber')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="invoiceDate"
                  label={t('page.user.cis.invoices.invoiceDate')}
                  value={state.invoiceDate}
                  clear={() => handleChangeCustom('invoiceDate')(null)}
                  required
                  error={errors.invoiceDate}
                >
                  <Datepicker
                    id="invoiceDate"
                    type="text"
                    small
                    placeholderText={t('page.user.cis.invoices.invoiceDatePlaceholder')}
                    selected={state.invoiceDate}
                    onChange={handleChangeCustom('invoiceDate', 'invoiceDate')}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    showMonthDropdown
                    locale="en-GB"
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="invoicePayedDate"
                  label={t('page.user.cis.invoices.invoicePayedDate')}
                  value={state.invoicePayedDate}
                  clear={() => handleChangeCustom('invoicePayedDate')(null)}
                  error={errors.invoicePayedDate}
                >
                  <Datepicker
                    id="invoicePayedDate"
                    type="text"
                    small
                    placeholderText={t('page.user.cis.invoices.invoicePayedDatePlaceholder')}
                    selected={state.invoicePayedDate}
                    onChange={handleChangeCustom('invoicePayedDate', 'invoicePayedDate')}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    showMonthDropdown
                    locale="fr"
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field id="vatId" label={t('page.user.cis.invoices.vatId')} error={errors.vatId}>
                  <input
                    id="vatId"
                    type="text"
                    placeholder={t('page.user.cis.invoices.vatIdPlaceholder')}
                    value={state.vatId}
                    onChange={handleChangeNative('vatId', 'vatId')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="memberAssociation"
                  label={t('page.user.cis.invoices.memberAssociation')}
                  value={ma.find((option) => option.value === state.memberAssociation) || ''}
                  clear={() => handleChangeSelect('memberAssociation')(null)}
                  required
                  error={errors.memberAssociation}
                >
                  <Select
                    id="memberAssociation"
                    type="text"
                    placeholder={t('page.user.cis.invoices.memberAssociation')}
                    value={ma.find((option) => option.value === state.memberAssociation) || ''}
                    options={ma}
                    onChange={handleChangeSelect('memberAssociation', 'memberAssociation')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="clientMemberAssociation"
                  label={t('page.user.cis.invoices.clientMemberAssociation')}
                  value={
                    clientMemberAssociations.find(
                      (option) => option.value === state.clientMemberAssociation,
                    ) || ''
                  }
                  clear={() => handleChangeSelect('clientMemberAssociation')(null)}
                  required
                  error={errors.clientMemberAssociation}
                >
                  <Select
                    id="clientMemberAssociation"
                    type="text"
                    placeholder={t('page.user.cis.invoices.clientMemberAssociation')}
                    value={
                      clientMemberAssociations.find(
                        (option) => option.value === state.clientMemberAssociation,
                      ) || ''
                    }
                    options={clientMemberAssociations}
                    onChange={handleChangeSelect(
                      'clientMemberAssociation',
                      'clientMemberAssociation',
                    )}
                    disabled={
                      !state.memberAssociation ||
                      !clientMemberAssociations ||
                      clientMemberAssociations.length === 0
                    }
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="dateFrom"
                  label={t('page.user.cis.invoices.dateFrom')}
                  value={state.dateFrom}
                  clear={() => handleChangeCustom('dateFrom')(null)}
                  error={errors.dateFrom}
                >
                  <Datepicker
                    id="dateFrom"
                    type="text"
                    small
                    placeholderText={t('page.user.cis.invoices.dateFromPlaceholder')}
                    selected={state.dateFrom}
                    onChange={handleChangeCustom('dateFrom', 'dateFrom')}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    showMonthDropdown
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="dateTo"
                  label={t('page.user.cis.invoices.dateTo')}
                  value={state.dateTo}
                  clear={() => handleChangeCustom('dateTo')(null)}
                  error={errors.dateTo}
                >
                  <Datepicker
                    id="dateTo"
                    type="text"
                    small
                    placeholderText={t('page.user.cis.invoices.dateToPlaceholder')}
                    selected={state.dateTo}
                    onChange={handleChangeCustom('dateTo', 'dateTo')}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    showMonthDropdown
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom={{ xs: 12, md: 0 }}>
                <Button type="button" onClick={() => submit()}>
                  {t('page.user.cis.invoices.createInvoice')}
                </Button>
              </Margin>
            </Col>
          </Row>
        </form>
      </Container>
    </PageContent>
  );
};

AddInvoice.propTypes = {
  errors: PropTypes.shape({
    referenceNumber: PropTypes.string,
    invoiceDate: PropTypes.string,
    invoicePayedDate: PropTypes.string,
    vatId: PropTypes.string,
    memberAssociation: PropTypes.string,
    clientMemberAssociation: PropTypes.string,
    dateFrom: PropTypes.string,
    dateTo: PropTypes.string,
  }),
  createInvoice: PropTypes.func.isRequired,
  removeError: PropTypes.func.isRequired,
  ma: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
};

AddInvoice.defaultProps = {
  errors: {},
};

const mapStateToProps = ({ ipma, globals }) => ({
  errors: ipma.cis.invoices.createErrors,
  ma: globals.availableMA,
  clientMemberAssociations: globals.cis.clientMemberAssociations,
});

const mapDispatchToProps = (dispatch) => ({
  createInvoice: (payload, history) => dispatch(createIpmaCisCreator('invoices', payload, history)),
  removeError: (payload) => dispatch(setIpmaCisCreateErrorsCreator('invoices', payload)),
  loadInvoicesAttributes: () => dispatch(loadGlobalCisInvoicesAttributesCreator()),
  loadClientMemberAssociations: (payload) =>
    dispatch(loadClientMemberAssociationsByMACreator(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddInvoice);
